<template>
  <div>
    <div class="page-head">
      <h1>Pass payment</h1>
      <p>Review your order and complete payment to activate your DSA Pass.</p>
    </div>

    <dsa-loading v-if="loading" text="Loading order…" />
    <template v-else-if="order">
      <div class="card pay-summary">
        <div class="row head">
          <span>Pass</span>
          <span class="strong">{{ passName }}</span>
        </div>
        <div class="row">
          <span>Standard fee</span>
          <span>{{ currency }} {{ standardFee }}</span>
        </div>
        <div class="row" v-if="reductionFee > 0">
          <span>Fee reduction</span>
          <span>({{ currency }} {{ reductionFee }})</span>
        </div>
        <div class="row">
          <span>Fee after reduction</span>
          <span class="strong">{{ currency }} {{ totalPrice }}</span>
        </div>
        <div class="row">
          <span>Payment service fee</span>
          <span>{{ currency }} {{ serviceFee }}</span>
        </div>
        <div class="divider" />
        <div class="row total">
          <span>Total order amount</span>
          <span class="strong accent">{{ currency }} {{ amount }}</span>
        </div>
        <p class="hint">*The service fee is charged by the payment platform. It is not included in the registration fee.</p>
      </div>

      <div class="section">
        <div class="section-head"><h2>Terms and conditions</h2></div>
        <div class="card terms-body" v-html="termsHtml" />
      </div>

      <div class="terms-agree">
        <label>
          <input v-model="isAgree" type="checkbox">
          I have read and agree to the terms and conditions.
        </label>
      </div>

      <div class="actions">
        <button type="button" class="btn btn-outline" @click="goBack">Cancel</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="paying"
          @click="goPay"
        >
          {{ paying ? 'Processing…' : `Pay ${currency} ${amount}` }}
        </button>
      </div>
    </template>
    <div v-else class="empty">Order not found.</div>
  </div>
</template>

<script>
import { getDsaPassOrder, confirmDsaPassPayment } from '@/api/dsa'
import { getUserId } from '@/utils/store'
import { resolvePassTermsHtml } from '@/constants/dsaPassTerms'

export default {
  name: 'StudentPassOrder',
  data() {
    return {
      loading: true,
      paying: false,
      order: null,
      isAgree: false,
    }
  },
  computed: {
    passName() {
      return this.order.pass_name
        || (this.order.pass && (this.order.pass.pass_name || this.order.pass.name))
        || 'DSA Registration Pass'
    },
    currency() {
      return this.order.currency || 'USD'
    },
    standardFee() {
      return this.order.standard_fee != null ? this.order.standard_fee : '—'
    },
    totalPrice() {
      return this.order.total_price != null ? this.order.total_price : 0
    },
    serviceFee() {
      return this.order.service_fee != null ? this.order.service_fee : 0
    },
    amount() {
      return Number(this.totalPrice) + Number(this.serviceFee)
    },
    reductionFee() {
      const std = Number(this.order.standard_fee)
      const tot = Number(this.order.total_price)
      if (!std || tot >= std) return 0
      return Math.round((std - tot) * 100) / 100
    },
    termsHtml() {
      return resolvePassTermsHtml()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      const order_id = this.$route.query.order_id
      const student_id = getUserId()
      if (!order_id) {
        this.loading = false
        return
      }
      try {
        const orderRes = await getDsaPassOrder(order_id, student_id)
        if (orderRes.data && orderRes.data.code === 0) {
          this.order = orderRes.data.data
        } else {
          this.$emit('toast', (orderRes.data && orderRes.data.msg) || 'Could not load order')
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load order')
      } finally {
        this.loading = false
      }
    },
    goBack() {
      this.$router.push('/student/pass')
    },
    async goPay() {
      if (!this.isAgree) {
        this.$emit('toast', 'Please read and agree to the terms')
        return
      }
      if (this.order.status === '已支付') {
        this.$emit('toast', 'Already paid')
        this.goBack()
        return
      }
      if (Number(this.order.total_price) <= 0) {
        this.paying = true
        try {
          const res = await confirmDsaPassPayment(this.order.id || this.order._id, getUserId())
          if (res.data && res.data.code === 0) {
            this.$emit('toast', 'Pass activated')
            this.$router.push({ path: '/student/dashboard', query: { paid: '1' } })
          } else {
            this.$emit('toast', (res.data && res.data.msg) || 'Payment failed')
          }
        } finally {
          this.paying = false
        }
        return
      }
      const url = this.$router.resolve({
        path: '/pass-pay',
        query: { order_id: this.order.id || this.order._id },
      })
      window.open(url.href, '_blank')
    },
  },
}
</script>

<style scoped>
.pay-summary .row{display:flex;justify-content:space-between;gap:16px;padding:10px 0;font-size:15px}
.pay-summary .row.head{font-weight:600;font-size:16px}
.pay-summary .strong{font-weight:600}
.pay-summary .accent{color:var(--blue);font-size:17px}
.pay-summary .divider{height:1px;background:var(--line);margin:8px 0}
.pay-summary .total{font-weight:600;font-size:16px}
.terms-body{font-size:14px;line-height:1.6;color:var(--gray)}
.terms-body >>> ul{margin:8px 0 12px 18px;padding:0}
.terms-body >>> li{margin-bottom:6px}
.terms-body >>> a{color:var(--blue)}
.terms-agree{margin-top:20px;font-size:14px}
.terms-agree label{display:flex;align-items:center;gap:10px;cursor:pointer}
.actions{display:flex;gap:12px;margin-top:24px;flex-wrap:wrap}
</style>
